import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "axios";
import { getAPIURL } from "../../../../utils/utils";

export const createReview = createAsyncThunk(
  "reviews/createReview",
  async ({ formData, token }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${getAPIURL()}/create/review`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errorMessage || 
        error.message || 
        "Failed to create review"
      );
    }
  }
);

export const getAllReview = createAsyncThunk(
  "reviews/getAllReview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`${getAPIURL()}/getAll/review`);
      return response.data.getAllReview;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        "Failed to fetch reviews"
      );
    }
  }
);

export const getReviewById = createAsyncThunk(
  "reviews/fetchById",
  async (reviewId, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${getAPIURL()}/getById/review/${reviewId}`
      );
      return response.data.reviewById;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        "Error fetching Review details"
      );
    }
  }
);

export const updateReview = createAsyncThunk(
  "reviews/update",
  async ({ reviewId, formData, token }, { rejectWithValue }) => {
    try {
      const response = await Axios.put(
        `${getAPIURL()}/update/review/${reviewId}`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errorMessage || 
        error.message || 
        "Error updating Review"
      );
    }
  }
);

export const deleteReview = createAsyncThunk(
  "reviews/deleteReview",
  async ({ reviewId, token }, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(
        `${getAPIURL()}/delete/review/${reviewId}`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errorMessage || 
        error.message || 
        "Failed to delete review"
      );
    }
  }
);

const reviewSlice = createSlice({
  name: "reviews",
  initialState: {
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: null,
    reviews: [],
    reviewById: null,
    updateSuccess: false,
    updateError: null,
    successMessage: null,
  },
  reducers: {
    resetReview: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.error = null;
      state.updateSuccess = false;
      state.updateError = null;
      state.successMessage = null;
    },
    clearUpdateStatus: (state) => {
      state.updateSuccess = false;
      state.updateError = null;
      state.successMessage = null;
      state.isSuccess = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Create Review
    builder.addCase(createReview.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
      state.isError = false;
      state.error = null;
    });
    builder.addCase(createReview.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
      state.error = null;
      state.successMessage = action.payload?.message || "Review created successfully";
    });
    builder.addCase(createReview.rejected, (state, action) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = true;
      state.error = action.payload || "Failed to create review";
    });

    // Get All Reviews
    builder.addCase(getAllReview.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
      state.error = null;
    });
    builder.addCase(getAllReview.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isError = false;
      state.error = null;
      state.reviews = action.payload;
    });
    builder.addCase(getAllReview.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.error = action.payload || "Failed to fetch reviews";
    });

    // Get Review By ID
    builder.addCase(getReviewById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getReviewById.fulfilled, (state, action) => {
      state.isLoading = false;
      state.reviewById = action.payload;
      state.error = null;
    });
    builder.addCase(getReviewById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to fetch review";
    });

    // Update Review
    builder.addCase(updateReview.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.updateSuccess = false;
      state.updateError = null;
      state.isSuccess = false;
    });
    builder.addCase(updateReview.fulfilled, (state, action) => {
      state.isLoading = false;
      state.updateSuccess = true;
      state.isSuccess = true;
      state.error = null;
      state.updateError = null;
      
      // Handle different message formats from API response
      let message = "Review updated successfully";
      
      if (action.payload?.message) {
        if (typeof action.payload.message === "string") {
          message = action.payload.message;
        } else if (Array.isArray(action.payload.message) && action.payload.message.length > 0) {
          // Handle array format like [{key: "success", value: "message"}]
          const firstMessage = action.payload.message[0];
          message = typeof firstMessage === "string" ? firstMessage : firstMessage?.value || message;
        } else if (typeof action.payload.message === "object" && action.payload.message.value) {
          // Handle object format like {key: "success", value: "message"}
          message = action.payload.message.value;
        }
      }
      
      state.successMessage = message;
      
      // Update the reviewById if it exists
      if (state.reviewById && action.payload?.review) {
        state.reviewById = action.payload.review;
      }
    });
    builder.addCase(updateReview.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to update review";
      state.updateError = action.payload || "Failed to update review";
      state.updateSuccess = false;
      state.isSuccess = false;
    });

    // Delete Review
    builder.addCase(deleteReview.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
      state.isError = false;
      state.error = null;
    });
    builder.addCase(deleteReview.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
      state.error = null;
      state.successMessage = action.payload?.message || "Review deleted successfully";
    });
    builder.addCase(deleteReview.rejected, (state, action) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = true;
      state.error = action.payload || "Failed to delete review";
    });
  },
});

export const { resetReview, clearUpdateStatus } = reviewSlice.actions;

export const selectReviewState = (state) => state.reviews;

export default reviewSlice.reducer;